import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, Printer, Calendar } from "lucide-react";
import CallSheetHtmlPreview from "../components/CallSheetHtmlPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public, no-login view of a published call sheet. Resolved via the
 * `public_token` set on first publish. Shows the same preview the owner sees.
 */
export default function PublicCallSheet() {
  const { token } = useParams();
  const [cs, setCs] = useState(null);
  const [project, setProject] = useState(null);
  const [crew, setCrew] = useState([]);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);
  const marked = useRef(false);

  useEffect(() => {
    axios.get(`${API}/public/callsheets/${token}`)
      .then(r => { setCs(r.data.callsheet); setProject(r.data.project); setCrew(r.data.crew || []); })
      .catch(e => setErr(e?.response?.data?.detail || "Call sheet not found"));
  }, [token]);

  useEffect(() => {
    if ((cs || err) && !marked.current) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        document.documentElement.setAttribute("data-print-ready", "1");
        marked.current = true;
      }));
    }
  }, [cs, err]);

  const printSheet = () => window.print();
  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/public/callsheets/${token}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `callsheet_${cs?.shoot_date || "callsheet"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("PDF download failed"); }
    finally { setDownloading(false); }
  };

  if (err) {
    return (
      <div data-testid="public-callsheet-error" className="flex min-h-screen items-center justify-center bg-[#0D0F12] px-6 text-center">
        <div className="max-w-md rounded-2xl border border-[#28303F] bg-[#14181F] p-10">
          <h1 className="font-[Outfit] text-2xl font-bold text-white">Call sheet unavailable</h1>
          <p className="mt-2 text-sm text-gray-400">This public link has expired, been revoked, or the call sheet is not yet published.</p>
        </div>
      </div>
    );
  }
  if (!cs) return <div className="flex min-h-screen items-center justify-center bg-[#0D0F12] text-amber-400 animate-pulse">Loading call sheet…</div>;

  return (
    <div className="min-h-screen bg-[#0D0F12]">
      <div className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-3 border-b border-[#28303F] bg-[#0D0F12]/90 px-4 py-3 backdrop-blur no-print">
        <div className="flex items-center gap-3">
          <Calendar className="text-amber-400" size={18} />
          <div>
            <p className="font-[Outfit] text-sm font-bold text-white">{cs.production_name || "Call Sheet"}</p>
            {cs.shoot_date && <p className="text-xs text-gray-500">{cs.shoot_date}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="public-print-btn" onClick={printSheet}
            className="flex items-center gap-2 rounded-lg border border-[#28303F] px-3 py-1.5 text-sm text-gray-300 transition-all hover:border-gray-500 hover:text-white">
            <Printer size={14}/> Print
          </button>
          <button data-testid="public-download-btn" onClick={downloadPDF} disabled={downloading}
            className="flex items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/20 px-3 py-1.5 text-sm text-blue-400 transition-all hover:bg-blue-500/30 disabled:opacity-50">
            <Download size={14}/> {downloading ? "..." : "PDF"}
          </button>
        </div>
      </div>

      <div className="flex min-w-0 justify-center p-3 sm:p-6">
        <div className="call-sheet-preview-canvas print-area w-full overflow-auto rounded-md shadow-2xl" style={{ maxWidth: "8.5in" }}>
          <CallSheetHtmlPreview
            cs={cs}
            accentColor={project?.accent_color}
            logoDataUri={cs?.logo_data_uri || project?.logo_data_uri}
            logoAlign={project?.logo_align}
            logoMaxHeightPt={project?.logo_max_height_pt}
            isDraft={false}
            crewDb={crew}
            deptOrder={project?.departments || []}
          />
        </div>
      </div>
    </div>
  );
}
